import { isSiteRegionCode } from '~/utils/societePayloadBuilder/regions';
import type { PayloadValidationResult } from '~/utils/societePayloadBuilder/types';
import { validateRequiredString } from '~/utils/societePayloadBuilder/validationHelpers';

import { CTA_PROMOTION, PROMO_CODE_PROMOTION } from '../constants';
import { validateCtaLink, validateOptionalCtaLink, validateOptionalExpiresAt } from '../payloadFields';

import type { LuxPlusPromoCard, LuxPlusPromoCardCarouselState } from './types';

export type LuxPlusPromoCardCarouselValidationResult = PayloadValidationResult;

function validationLabel(sectionLabel: string, fieldLabel: string): string {
  return `${sectionLabel} ${fieldLabel}`;
}

function validateEligibleRegions(regions: Array<string>, label: string, errors: Array<string>) {
  if (regions.length === 0) {
    errors.push(`${validationLabel(label, 'Eligible Regions')} requires at least one region`);
    return;
  }

  const invalidRegions = regions.filter((region) => !isSiteRegionCode(region));
  if (invalidRegions.length > 0) {
    errors.push(`${validationLabel(label, 'Eligible Regions')} has invalid codes: ${invalidRegions.join(', ')}`);
  }
}

function validatePromo(promo: LuxPlusPromoCard, errors: Array<string>) {
  const label = promo.draftLabel;
  validateRequiredString(promo.label, validationLabel(label, 'Label'), errors);
  validateRequiredString(promo.title, validationLabel(label, 'Title'), errors);
  validateRequiredString(promo.subtitle, validationLabel(label, 'Subtitle'), errors);
  validateEligibleRegions(promo.eligibleRegions, label, errors);
  validateOptionalExpiresAt(promo.expiresAt, validationLabel(label, 'Expires at'), errors);

  if (promo.type === PROMO_CODE_PROMOTION) {
    validateRequiredString(promo.promoCode, validationLabel(label, 'Promo code'), errors);
    validateRequiredString(promo.termsAndConditions, validationLabel(label, 'Terms and conditions'), errors);
    validateOptionalCtaLink(promo.primaryActionHref, validationLabel(label, 'Primary action URL'), errors);
    return;
  }

  if (promo.type === CTA_PROMOTION) {
    validateRequiredString(promo.ctaText, validationLabel(label, 'CTA text'), errors);
    validateCtaLink(promo.ctaLink, validationLabel(label, 'CTA URL'), errors);
  }
}

export function validatePromoCardCarouselPayload(
  state: LuxPlusPromoCardCarouselState,
): LuxPlusPromoCardCarouselValidationResult {
  const errors: Array<string> = [];

  if (state.promos.length === 0) {
    errors.push('Promo cards requires at least one promo');
  }

  state.promos.forEach((promo) => validatePromo(promo, errors));

  return {
    isValid: errors.length === 0,
    errors,
  };
}
